import { memo, useCallback, useMemo, useState } from "react";
import { Fade, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import { useSnackbar } from "@/components/Snackbar";
import { TopicsSearchFilter } from "@/lib/company-filter/modules/intent/components/filters";
import { TopicUploadDialog } from "@/lib/company-filter/modules/intent/components/TopicUploadDialog";
import { useBulkTopicOperations } from "@/lib/company-filter/modules/intent/hooks/useBulkTopicOperations";
import { MAX_INTENT_TOPICS } from "@/lib/company-filter/modules/intent/slice";
import type { CachedTopic } from "@/types/intent";

export interface IntentTopicSelectorProps {
  selectedTopicIds: string[];
  topicsCache: CachedTopic[];
  onSelectionChange: (topicIds: string[], cache: CachedTopic[]) => void;
  showUploadDownload?: boolean;
  showToolbarLabel?: boolean;
  maxTopics?: number;
  mx?: number;
}

const MemoizedTopicsSearchFilter = memo(TopicsSearchFilter);
MemoizedTopicsSearchFilter.displayName = "MemoizedTopicsSearchFilter";

export const IntentTopicSelector: React.FC<IntentTopicSelectorProps> = ({
  selectedTopicIds,
  topicsCache,
  onSelectionChange,
  showUploadDownload = true,
  showToolbarLabel = true,
  maxTopics = MAX_INTENT_TOPICS,
  mx = 4,
}) => {
  const { showWarning } = useSnackbar();
  const { handleDownload } = useBulkTopicOperations();

  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const handleUploadSuccess = useCallback(
    (uploadedTopics: CachedTopic[]) => {
      const existingIds = new Set(selectedTopicIds);
      const newTopics = uploadedTopics.filter(
        (topic) => !existingIds.has(topic.id)
      );

      if (newTopics.length === 0) return;

      const remainingSlots = maxTopics - selectedTopicIds.length;
      if (remainingSlots <= 0) {
        showWarning(
          `You can select up to ${maxTopics.toLocaleString()} intent topics. Remove some topics to add more.`
        );
        return;
      }

      const topicsToAdd = newTopics.slice(0, remainingSlots);
      if (topicsToAdd.length < newTopics.length) {
        showWarning(
          `Only ${topicsToAdd.length.toLocaleString()} of ${newTopics.length.toLocaleString()} topics were added. Intent topics are limited to ${maxTopics.toLocaleString()}.`
        );
      }

      const nextIds = [...selectedTopicIds, ...topicsToAdd.map((t) => t.id)];

      const cacheById = new Map(topicsCache.map((t) => [t.id, t]));
      topicsToAdd.forEach((t) => cacheById.set(t.id, t));
      const nextCache = nextIds
        .map((id) => cacheById.get(id))
        .filter((t): t is CachedTopic => !!t);

      onSelectionChange(nextIds, nextCache);
    },
    [maxTopics, onSelectionChange, selectedTopicIds, showWarning, topicsCache]
  );

  const handleDownloadClick = useCallback(() => {
    handleDownload(topicsCache);
  }, [handleDownload, topicsCache]);

  const headerActions = useMemo(() => {
    if (!showUploadDownload) return null;

    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Tooltip title="Upload topics from CSV">
          <IconButton size="small" onClick={() => setIsUploadDialogOpen(true)}>
            <UploadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            selectedTopicIds.length === 0
              ? "Select topics to download"
              : "Download selected topics"
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={selectedTopicIds.length === 0}
              onClick={handleDownloadClick}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }, [handleDownloadClick, selectedTopicIds.length, showUploadDownload]);

  return (
    <>
      <Fade in timeout={300}>
        <Stack spacing={1} sx={{ mx }}>
          {headerActions && (
            <Stack
              direction="row"
              justifyContent={showToolbarLabel ? "space-between" : "flex-end"}
              alignItems="center"
            >
              {showToolbarLabel && (
                <Typography variant="subtitle2" color="text.secondary">
                  Selected topics
                </Typography>
              )}
              {headerActions}
            </Stack>
          )}

          <MemoizedTopicsSearchFilter
            selectedTopicIds={selectedTopicIds}
            topicsCache={topicsCache}
            onSelectionChange={onSelectionChange}
            maxTopics={maxTopics}
          />
        </Stack>
      </Fade>

      <TopicUploadDialog
        open={isUploadDialogOpen}
        onClose={() => setIsUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </>
  );
};
